import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { Usuario } from '../tiposGlobais/menu';
import { authService } from '../servicesGlobais/authService';

interface UsuarioContextType {
  usuario: Usuario | null;
  setUsuario: (usuario: Usuario | null) => void;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const UsuarioContext = createContext<UsuarioContextType | undefined>(undefined);

interface UsuarioProviderProps {
  children: ReactNode;
}

export function UsuarioProvider({ children }: UsuarioProviderProps) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Verificar autenticação ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          // Buscar dados do usuário autenticado via API
          // O endpoint /Auth/me já valida o token automaticamente via [Authorize]
          const usuarioData = await authService.obterUsuarioAutenticado();
          // Garantir que papeis seja sempre um array de strings
          const papeis = usuarioData.papeis || [];
          const papeisNormalizados = Array.isArray(papeis) 
            ? papeis.map((p: any) => String(p)) 
            : [];
          
      setUsuario({
        ...usuarioData,
        papeis: papeisNormalizados,
        pessoaId: usuarioData.pessoaId,
        papeisNomes: usuarioData.papeisNomes || [],
        fotoUrl: usuarioData.fotoUrl
      });
        } catch (err) {
          // Se retornar 401, o token é inválido ou expirado
          setError('Sessão expirada. Faça login novamente.');
          authService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authService.login({ email, senha: senha });
      
      if (!response || !response.usuario) {
        throw new Error('Resposta inválida do servidor');
      }
      
      // Garantir que papeis seja sempre um array de strings
      const papeis = response.usuario.papeis || [];
      const papeisNormalizados = Array.isArray(papeis) 
        ? papeis.map((p: any) => String(p)) 
        : [];
      
      setUsuario({
        id: response.usuario.id,
        nome: response.usuario.nome,
        email: response.usuario.email,
        ativo: response.usuario.ativo,
        papeis: papeisNormalizados,
        papeisNomes: response.usuario.papeisNomes || [],
        pessoaId: response.usuario.pessoaId,
        fotoUrl: response.usuario.fotoUrl
      });
      
      // Invalidar cache do menu para atualizar com permissões do novo usuário
      queryClient.invalidateQueries(['menu']);
      
      return true;
    } catch (err: any) {
      // Capturar mensagem de erro do backend se disponível
      const errorMessage = err?.message || err?.response?.data || 'Email ou senha inválidos';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUsuario(null);
    setError(null);
    queryClient.clear(); // Limpar cache do React Query
  };

  return (
    <UsuarioContext.Provider value={{
      usuario,
      setUsuario,
      login,
      logout,
      isAuthenticated: !!usuario,
      loading,
      error
    }}>
      {children}
    </UsuarioContext.Provider>
  );
}

export function useUsuario() {
  const context = useContext(UsuarioContext);
  if (context === undefined) {
    throw new Error('useUsuario deve ser usado dentro de um UsuarioProvider');
  }
  return context;
}
